import { useDispatch, useSelector } from "react-redux"
import BasicModule from "../../Commons/BasicModule"
import AuthCMS from "../../Layouts/AuthCMS"
import { getList, getState, checkItem, activeItem, inActiveItem, updatePassword } from "../../Stores/Proxy/Slice"
import { useEffect, useRef, useState } from "react"
import { CheckCircleOutlined, CloseCircleOutlined, EnterOutlined, CheckCircleTwoTone } from '@ant-design/icons'
import { Alert, Tooltip, Typography, Button, message, Card, Row, Col, Spin, Badge, Popover } from 'antd'
import './Proxy.css'

import { getList as getListLine, getState as getStateLine } from "../../Stores/Line/Slice"

const { Text } = Typography

const modelField = ['id', 'ip_address', 'city_name', 'line_name', 'keys']

const columnTable = modelField.map((field) => ({
    title: (() => {
        switch (field) {
            case 'ip_address': return 'IP';
            case 'port': return 'Port';
            case 'socks5': return 'Socks5';
            case 'city_name': return 'City';
            case 'is_primary': return 'Primary';
            case 'line_name': return 'Line';
            case 'keys': return 'Keys';
            default: return field.toUpperCase();
        }
    })(),
    dataIndex: field,
    key: field,
    render: (text, record) => {
        if (field === 'ip_address') {
            return (
                <Tooltip title={`${record.ip_address}:${record.port} - ${record.socks5}`}>
                    <Text>
                        {record.ip_address_dup ? <span style={{ background: "yellow" }}>{record.ip_address}</span> : <span>{record.ip_address}</span>}
                    </Text>
                </Tooltip>
            )
        }
        if (field === 'id') {
            return (
                <Button
                    type="link"
                    onClick={() => {
                        navigator.clipboard.writeText(text)
                        message.success('ID copied to clipboard')
                    }}
                >
                    {text}
                </Button>
            )
        }
        if (field === 'is_primary') {
            return record.is_primary ? <CheckCircleOutlined style={{ color: 'gold' }} /> : <CloseCircleOutlined style={{ color: 'silver' }} />
        }
        if (field === 'noti') {
            return record.noti ? <Text type="success">Success</Text> : null;
        }
        if (field === 'keys') {
            return record.keys ? <Popover content={record.keys.map((key) => (
                <div key={key.key_id}>{key.key_id}</div>
            ))} title="Keys">
                <Badge count={record.keys.length} showZero color="#faad14" />
            </Popover> : <></>
        }
        return text
    }
}))

const expandableTable = {
    expandedRowRender: (record) => (
        <div className="flex">
            <div style={{ transform: "scale(-1, 1)", alignContent: "center" }}>
                <EnterOutlined style={{ fontSize: 26, color: "#ccc" }} />
            </div>
            <div className="flex flex-col">
                {
                    record.noti && record.noti.length > 0 ? (
                        record.noti.map((item, index) => (
                            item.status == "error" || item.status == "inactive" ?
                                <Alert key={index} style={{ marginTop: 8 }} message={JSON.stringify(item)} type="error" showIcon />
                                :
                                <Alert key={index} style={{ marginTop: 8 }} message={JSON.stringify(item)} type="success" showIcon />
                        ))
                    ) : (
                        <Alert style={{ marginTop: 8 }} message="Success" type="success" showIcon />
                    )
                }
            </div>
        </div>
    ),
    rowExpandable: (record) => record.noti && record.noti.length > 0,
    showExpandColumn: true,
    defaultExpandAllRows: false,
}
// Load filters from localStorage
const savedFilters = JSON.parse(localStorage.getItem("tableFilters")) || {};

const Proxy = () => {
    const data = useSelector(getState)
    const dataLine = useSelector(getStateLine)
    const dispatch = useDispatch();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([])
    const [dataTable, setDataTable] = useState([])
    const [count_private_fixed_ava, setCountPrivateFixedAva] = useState(0)
    const [count_private_rotate_ava, setCountPrivateRotateAva] = useState(0)
    const [count_public_fixed_ava, setCountPublicFixedAva] = useState(0)
    const [count_public_rotate_ava, setCountPublicRotateAva] = useState(0)
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [duplicateIPs, setDuplicateIPs] = useState([])
    const [keySearch, setKeySearch] = useState("");

    const [customColumn, setCustomColumn] = useState([...columnTable, {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        filters: [
            {
                text: 'Active',
                value: true,
            },
            {
                text: 'Inactive',
                value: false,
            },
        ],
        onFilter: (value, record) => record.status == value,
        defaultFilteredValue: savedFilters.status || [],
        render: (record) => record ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleOutlined style={{ color: 'red' }} />
    },
    {
        title: 'Live',
        dataIndex: 'refresh_status',
        key: 'refresh_status',
        width: 100,
        filters: [
            {
                text: 'Success',
                value: 'success',
            },
            {
                test: 'Pending',
                value: 'pending'
            }
        ],
        onFilter: (value, record) => record.refresh_status == value,
        defaultFilteredValue: savedFilters.refresh_status || [],
        render: (record) => record == "success" ? <CheckCircleOutlined style={{ color: 'blue' }} /> : <CloseCircleOutlined style={{ color: 'grey' }} />
    }
    ])

    useEffect(() => {
        let newData = data.proxies.filter((e) => {
            let found = false;
            for (const [key, value] of Object.entries(e)) {
                if (value && value.toString().toLowerCase().includes(keySearch.toLowerCase())) {
                    found = true;
                    break;
                }
            }
            return found;
        });
        setDataTable(newData)
    }, [data, keySearch])

    useEffect(() => {
        dispatch(getList())
        // Get Filter: get lines
        dispatch(getListLine())
    }, [dispatch])

    useEffect(() => {
        // Update data to filter Lines
        let filters = dataLine.lines.map((line) => {
            return {
                text: line.name,
                value: line.name,
            }
        })
        if (filters.length) {
            setCustomColumn((prevColumns) =>
                prevColumns.map((col) =>
                    col.title === 'Line'
                        ? {
                            ...col,
                            filters,
                            onFilter: (value, record) => {
                                return record.line_name === value
                            },
                            defaultFilteredValue: savedFilters.line_name || [],
                        } : col
                )
            );
        }
    }, [dataLine])

    useEffect(() => {
        setDataTable(data.proxies)
        setCount(data.count)
        setCountPrivateFixedAva(data.count_private_fixed_ava)
        setCountPrivateRotateAva(data.count_private_rotate_ava)
        setCountPublicFixedAva(data.count_public_fixed_ava)
        setCountPublicRotateAva(data.count_public_rotate_ava)

        // Find duplicate IPs
        const ipCounts = {};
        data.proxies.forEach(proxy => {
            if (!ipCounts[proxy.ip_address]) {
                ipCounts[proxy.ip_address] = { count: 0, ids: [] };
            }
            ipCounts[proxy.ip_address].count += 1;
            ipCounts[proxy.ip_address].ids.push(proxy.id);
        });
        const duplicates = Object.keys(ipCounts).filter(ip => ipCounts[ip].count > 1);
        // const duplicates = ['115.74.97.159']
        Object.keys(ipCounts).map(ip => {
            if (ipCounts[ip].count > 1) {
                // Dupplicate IP
                ipCounts[ip].ids.map((id) => {
                    updateDataTable({
                        id: id,
                        addField: {
                            ip_address_dup: 1,
                        },
                    })
                })
            }
        })
        setDuplicateIPs(duplicates);
    }, [data.proxies])

    const rowSelection = {
        type: "checkbox",
        selectedRowKeys,
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectedRows(selectedRows)
        },
    };

    const updateDataTable = ({ id, addField = false }) => {
        setDataTable(prevDataTable =>
            prevDataTable.map(item =>
                item.id === id
                    ? { ...item, ...addField }
                    : item
            )
        );
    }

    const actionSelection = [
        {
            label: "Active",
            func: () => {
                let rowActive = selectedRows.filter(row => row.status === true)
                if (rowActive.length > 0) {
                    message.error('Please select deactive proxy')
                    return
                }
                const selectedIds = selectedRows.map(row => row.id)
                setLoading(true)
                dispatch(activeItem(selectedIds)).then(res => {
                    setSelectedRowKeys([]);
                    const { data } = res.payload
                    if (data.length == 0) {
                        selectedIds.map(id => {
                            updateDataTable({
                                id: id,
                                addField: {
                                    noti: [{ "message": "Error", "status": "error" }]
                                },
                            })
                        })
                    }
                    data.forEach(item => {
                        const { proxy_id, messages, status, ip_address, refresh_status } = item;
                        const hasError = messages.some(msg => msg.status === "error");
                        updateDataTable({
                            id: proxy_id,
                            addField: {
                                noti: messages,
                                status: status,
                                ip_address: ip_address,
                                refresh_status: refresh_status
                            },
                        })
                    })
                    setLoading(false)
                }).catch(() => setLoading(false))
            },
            color: "green" // Add color property
        },
        {
            label: "Deactive",
            func: () => {
                let rowInActive = selectedRows.filter(row => row.status === false)
                if (rowInActive.length > 0) {
                    message.error('Please select active proxy')
                    return
                }
                const selectedIds = selectedRows.map(row => row.id)
                setLoading(true)
                dispatch(inActiveItem(selectedIds)).then(res => {
                    setSelectedRowKeys([]);
                    const { data } = res.payload
                    if (data.length == 0) {
                        selectedIds.map(id => {
                            updateDataTable({
                                id: id,
                                addField: {
                                    noti: [{ "message": "Error", "status": "error" }],
                                },
                            })
                        })
                    }
                    data.forEach(item => {
                        const { proxy_id, messages, status, refresh_status, ip_address } = item;
                        updateDataTable({
                            id: proxy_id,
                            addField: {
                                noti: messages,
                                status: status,
                                refresh_status: refresh_status,
                                ip_address: ip_address
                            },
                        })
                    })
                    setLoading(false)
                }).catch(() => setLoading(false))
            },
            color: "red" // Add color property
        },
        {
            label: "Check",
            func: async () => {
                let rowInActive = selectedRows.filter(row => row.status === false)
                if (rowInActive.length > 0) {
                    message.error('Please select active proxy')
                    return
                }
                const selectedIds = selectedRows.map(row => row.id)
                setLoading(true)
                dispatch(checkItem(selectedIds)).then(res => {
                    setSelectedRowKeys([]);
                    const { data } = res.payload
                    console.log(data)
                    if (data.length == 0) {
                        selectedIds.map(id => {
                            updateDataTable({
                                id: id,
                                addField: {
                                    noti: [{ "message": "server return empty!" }]
                                },
                            })
                        })
                    }
                    data.forEach(item => {
                        const { proxy_id, messages, status, refresh_status, ip_address } = item;
                        updateDataTable({
                            id: proxy_id,
                            addField: {
                                noti: messages,
                                refresh_status: refresh_status,
                                ip_address: ip_address
                            }
                        })
                    })
                    setLoading(false)
                }).catch(() => setLoading(false))
            },
            color: "blue" // Add color property
        },
        {
            label: "Update Password",
            func: async () => {
                if (selectedRows.length === 0) {
                    message.error('Please select at least one proxy');
                    return;
                }
                const selectedIds = selectedRows.map(row => row.id);
                setLoading(true);
                try {
                    const response = await dispatch(updatePassword(selectedIds)); // Assuming `updatePassword` is an action in your slice
                    const { data } = response.payload;
                    if (data.length === 0) {
                        selectedIds.forEach(id => {
                            updateDataTable({
                                id: id,
                                addField: {
                                    noti: [{ "message": "Password update failed", "status": "error" }]
                                },
                            });
                        });
                    } else {
                        data.forEach(item => {
                            const { proxy_id, messages } = item;
                            updateDataTable({
                                id: proxy_id,
                                addField: {
                                    noti: messages,
                                },
                            });
                        });
                    }
                    message.success('Password updated successfully');
                } catch (error) {
                    message.error('Failed to update password');
                } finally {
                    setLoading(false);
                }
            },
            color: "orange" // Add color property
        }
    ]

    const handleChangeTable = (pagination, filters, sorter) => {
        // Save filters to localStorage
        localStorage.setItem("tableFilters", JSON.stringify(filters));
    };

    return <AuthCMS tabActive={"/proxy"}>
        <Row gutter={[16, 16]}>
            <Col span={4}>
                <Card title="Total Proxies" bordered={false}>
                    <div className="card-content">{count}</div>
                </Card>
            </Col>
            <Col span={4}>
                <Card title="Proxy Private Fixed" bordered={false}>
                    <div className="card-content">{count_private_fixed_ava}</div>
                </Card>
            </Col>
            <Col span={4}>
                <Card title="Proxy Private Rotate" bordered={false}>
                    <div className="card-content">{count_private_rotate_ava}</div>
                </Card>
            </Col>
            <Col span={4}>
                <Card title="Proxy Public Fixed" bordered={false}>
                    <div className="card-content">{count_public_fixed_ava}</div>
                </Card>
            </Col>
            <Col span={4}>
                <Card title="Proxy Public Rotate" bordered={false}>
                    <div className="card-content">{count_public_rotate_ava}</div>
                </Card>
            </Col>
        </Row>
        <Spin spinning={loading}>
            <BasicModule
                columnTable={customColumn}
                dataList={dataTable}
                modelField={modelField}
                selectedRows={selectedRows}
                rowSelection={rowSelection}
                actionSelection={actionSelection}
                expandableTable={expandableTable}
                rowClassName={record => duplicateIPs.includes(record.ip_address) ? 'duplicate-row' : ''}
                setKeySearch={setKeySearch}
                handleChangeTable={handleChangeTable}
            />
        </Spin>
    </AuthCMS>
}

export default Proxy